/**
 * Die Reihenfolge der Seiten und ihre Nummern.
 *
 * Das Buch ist ein linear geordnetes PDF, Seite 1 bis N. Was daraus wird -
 * geheftet, gebunden, gelocht - ist ausdrücklich nicht Sache der App; sie
 * liefert die Seitenfolge und sonst nichts. Deshalb gibt es hier auch keine
 * Rücksicht auf gerade und ungerade Seiten, keine Vakatseiten und keinen Bund.
 *
 * Die Nummern werden hier vergeben und nicht dem Browser überlassen. Der
 * naheliegende Weg wäre counter(page) in einer @page-Margin-Box, und der
 * funktioniert auch - aber counter(pages) für das "von N" ist historisch
 * unzuverlässig, Seitenzahlen in position:fixed zeigen in Chromium konstant 1,
 * und laufende Kopfzeilen über Margin-Boxen gibt es erst ab Chromium 131. Da
 * ohnehin je gedruckter Seite genau ein Element im DOM steht, ist die
 * verlässlichste Lösung die einfachste: die Nummer steht als Text in der
 * Seite, gezählt von dieser Funktion.
 */

import { TULIP_SHAPES, type TulipShapeInfo } from '../../lib/tulip'

/**
 * Schlüssel der Übersichtskarte.
 *
 * Steht hier und nicht bei den Kartenausschnitten, damit ihn auch die
 * Oberfläche benutzen kann, ohne MapLibre mitzuladen: mapShots.ts zieht die
 * ganze Kartenbibliothek herein und wird deshalb erst beim Rendern nachgeladen.
 * Keine Etappen-ID kollidiert damit - die beginnen alle mit "s".
 */
export const UEBERSICHT_KEY = 'uebersicht'

/**
 * Wie viele Etappen die Übersichtsliste trägt.
 *
 * Zwei Spalten zu acht Zeilen, genau so wie .dr-etappenliste in print.css es
 * setzt (grid-template-columns: 1fr 1fr, grid-template-rows: repeat(8,
 * min-content), grid-auto-flow: column). Beide gehören zusammen angefasst.
 */
export const UEBERSICHT_PLAETZE = 16

/** Die Etappen der Übersichtsseite und die, die nicht mehr daraufpassen. */
export interface UebersichtsListe<T> {
  gezeigt: T[]
  /** Wie viele Etappen die Liste nicht zeigt. */
  rest: number
}

/**
 * Kürzt die Etappenliste der Übersichtsseite auf das, was das Raster trägt.
 *
 * Die Kürzung selbst gab es vorher schon - nur machte sie das Raster, stumm.
 * Überzählige Zeilen legten eine dritte, nicht vorgesehene Spalte an, die je
 * nach Länge der Ortsnamen entweder die beiden anderen zusammenquetschte oder
 * seitlich hinter overflow:hidden verschwand; ab der 25. Etappe zuverlässig,
 * mit langen Namen früher.
 *
 * Dass sie hier passiert und nicht im Raster, ist der ganze Punkt: so gibt es
 * eine Zahl, die auf dem Papier stehen kann. Die Übersicht ist die Seite, auf
 * der man nachsieht, was noch kommt - wird unterwegs eine Etappe eingeschoben,
 * weil eine Strasse gesperrt ist, darf sie nicht spurlos verschwinden.
 */
export function uebersichtsListe<T>(stages: readonly T[]): UebersichtsListe<T> {
  return {
    gezeigt: stages.slice(0, UEBERSICHT_PLAETZE),
    rest: Math.max(0, stages.length - UEBERSICHT_PLAETZE),
  }
}

/**
 * Wie viele Zeichen eine Legendenseite trägt.
 *
 * Zwei Zahlen, weil es zwei Seiten gibt und nur die erste die Tafel mit dem
 * Bauprinzip trägt. Beide sind nicht gerechnet, sondern am gerenderten Buch
 * abgelesen (A5 bei 96 dpi): die Seite wurde mit Kopien aufgefüllt, bis die
 * unterste Reihe unter die Kante des Seitenkoerpers rutschte.
 *
 *     mit Tafel     12 Plätze, 72.5 px Luft   ->  13 stehen 5.2 px darunter
 *     ohne Tafel    16 Plätze, 28.7 px Luft   ->  17 stehen 48.9 px darunter
 *
 * Die Masse dahinter, für den, der es nachrechnen will:
 *
 *     Seitenkoerper                          690.5 px
 *     Titelzeile samt Abstand                 38.0
 *     Tafel samt Rahmen und Abstand          111.6   (nur erste Seite)
 *     eine Reihe                              77.7   (5.4 em Zeichen + Polster)
 *     zwei Spalten je Reihe
 *
 * Vorher stand hier eine einzige Zahl, vierzehn, und der Katalog hatte
 * dreizehn Formen - ein Platz frei. Mit Fähre und Schotter sind es fünfzehn.
 * Auf einer Seite legte die fünfzehnte eine achte Reihe an, und die stünde
 * 84.2 px unter der Kante: stillschweigend weg, wie schon dreimal in dieser
 * Datei und in print.css.
 *
 * Warum eine zweite Seite und nicht engere Reihen oder eine kleinere Tafel:
 * beide Wege wurden nachgemessen und geben zusammen nicht genug her.
 *
 *  - Engere Reihen. Die Reihenhöhe ist die Höhe des Zeichens darin, 5.4 em -
 *    dieselben 5.4 em wie auf der Roadbookseite, und das ist keine
 *    Formatierung, sondern der Sinn der Legende: eine Legende in einer anderen
 *    Grösse erklärt ein anderes Zeichen. Zu holen ist nur das Polster, 0.18 em
 *    oben und unten, also 4.8 px je Reihe. Achtmal sind das 38.4 px von 84.2.
 *  - Kleinere Tafel. Die restlichen 45.8 px müsste sie hergeben: von 111.6 auf
 *    65.8 px. Sie ist 300 auf 104 Einheiten breit, ihre Höhe hängt also an
 *    ihrer Spaltenbreite - 65.8 px hoch heisst rund 154 px breit, und ihre
 *    Beschriftungen stünden dann in gut fünf Pixeln, keine vier Punkt. Eine
 *    Anleitung, die man nicht lesen kann, ist keine.
 *
 * Also zwei Seiten. Geteilt wird nicht nach Platz, sondern nach dem, was das
 * Zeichen sagt - siehe legendeSeiten. Genutzt sind heute 7 und 8 Plätze; die
 * Reserve ist damit zum ersten Mal grösser als null, und zwar auf beiden
 * Seiten.
 */
export const LEGENDE_PLAETZE_MIT_TAFEL = 12
export const LEGENDE_PLAETZE_OHNE_TAFEL = 16

/**
 * Wie lang der Erklärsatz eines Zeichens höchstens sein darf.
 *
 * Hundertdreissig Zeichen. Die Rechnung: neben dem 19 mm breiten Bild bleiben
 * in der halben Spalte rund 40 mm, in 0.66 em gesetzt sind das gemessen 33
 * Zeichen je Zeile. Über dem Satz steht der Name, unter ihm darf nichts mehr
 * kommen - es bleiben vier Zeilen, also 132 Zeichen, und hier steht 130.
 *
 * Ohne diesen Deckel wäre der fünfte Satz einer Erklärung stillschweigend weg:
 * die Reihe hat eine feste Höhe, und was darüber hinausgeht, schneidet
 * overflow:hidden ab. Derselbe Fehler wie bei den Planungsspalten der
 * Etappenseite, nur an einer Stelle, an der es niemandem auffiele, weil die
 * Legende nach dem Schreiben nie wieder gelesen wird.
 */
export const LEGENDE_ZEICHEN_MAX = 130

/**
 * Wie viele Einträge eine Checklistenseite trägt.
 *
 * Zwei Zahlen, weil es zwei Listen mit zwei verschiedenen Zeilenhöhen sind:
 * eine Vignette bringt Preis und Notiz mit, ein Ersatzteil nur seinen Namen.
 * Beide sind nicht gerechnet, sondern am gerenderten Buch abgelesen (A5 bei
 * 96 dpi, Seitenkoerper 617.3 px), und beide messen den ungünstigen Fall -
 * jeden Eintrag so lang, dass die Deckel in print.css greifen:
 *
 *     Vignette, Kopfzeile + zweizeilige Notiz    52.4 px
 *     Ersatzteil, nur Kopfzeile                  23.9 px
 *     Restzeile "und N weitere"                  22.9 px
 *
 * Gemessen wurde, indem die Seite mit Kopien aufgefüllt wurde, bis die
 * unterste Zeile unter die Kante des Seitenkoerpers rutschte:
 *
 *     Vignetten     10 Plätze + Restzeile, 28.1 px Luft  ->  11 stehen 24.3 px darunter
 *     Ausrüstung    23 Plätze + Restzeile,  3.0 px Luft  ->  24 stehen 20.9 px darunter
 *
 * Die Restzeile ist mitgemessen: sie steht nur da, wenn gekürzt wurde, und
 * genau dann ist die Seite voll.
 *
 * Vorher gab es diese Zahlen nicht, und die Seite nahm, was kam. Gemessen an
 * derselben Seite standen von zwanzig Vignetten vierzehn da; ab der
 * fünfzehnten fiel jede weitere ersatzlos unter die Kante, bei der
 * Ausrüstungsliste ab der fünfundzwanzigsten - und der Zähler oben zeigte
 * weiter die volle Zahl. Derselbe Fehler wie bei den Planungsspalten der
 * Etappenseite, bei der Übersichtsliste, bei den Tulpenreihen und bei der
 * Legende.
 *
 * Dass eine Zeile wirklich nicht höher wird als gemessen, ist keine Annahme:
 * .dr-check-kopf und .dr-check-text small in print.css kürzen sie sichtbar auf
 * eine beziehungsweise zwei Zeilen. Beide gehören zusammen angefasst.
 */
export const CHECKS_PLAETZE_VIGNETTEN = 10
export const CHECKS_PLAETZE_AUSRUESTUNG = 23

/** Die Einträge einer Checklistenseite und die, die nicht mehr daraufpassen. */
export interface ChecklistenSeite<T> {
  gezeigt: T[]
  /** Wie viele Einträge die Seite nicht zeigt. */
  rest: number
}

/**
 * Kürzt eine Checkliste auf das, was die Seite trägt.
 *
 * Der Zähler in der Überschrift zählt weiter über die ganze Liste - er sagt,
 * wie viel erledigt ist, und das ist eine Aussage über die Reise und nicht
 * über die Seite. Was die Seite nicht zeigt, sagt stattdessen die Restzeile.
 * Beides zusammen ist die Auskunft: "13 von 30 erledigt, und 19 stehen hier
 * nicht."
 *
 * @param eintraege Die Einträge der Liste, in ihrer Reihenfolge.
 * @param plaetze Wie viele davon auf die Seite passen.
 */
export function checklistenSeite<T>(eintraege: readonly T[], plaetze: number): ChecklistenSeite<T> {
  return {
    gezeigt: eintraege.slice(0, plaetze),
    rest: Math.max(0, eintraege.length - plaetze),
  }
}

/** Eine Legendenseite: ob sie die Tafel trägt und welche Zeichen darauf stehen. */
export interface LegendeSeite {
  /**
   * Trägt diese Seite die Tafel mit dem Bauprinzip? Nur die erste.
   *
   * Die Tafel erklärt den Pfeil - "so geht es weiter" -, und sie steht deshalb
   * bei den Zeichen, die einen Pfeil in eine Richtung setzen. Auf der zweiten
   * Seite hätte sie nichts zu erklären, was dort zu sehen wäre.
   */
  mitTafel: boolean
  zeichen: TulipShapeInfo[]
}

/**
 * Wie sich der Katalog auf die Legendenseiten verteilt.
 *
 * Geteilt wird an der Frage, die der Leser im Auto hat: sagt mir dieses
 * Zeichen, wohin ich fahren muss, oder sagt es mir, was gleich kommt? Das ist
 * genau die Auskunft, die im Katalog schon steht - directions ist leer, wenn
 * die Form keine Wahl kennt und die Strasse geradeaus durchläuft.
 *
 * Erste Seite also: Kreisverkehr, Kreuzung, T-Kreuzung, Gabelung, Abzweig und
 * die beiden Autobahnrampen - sieben Zeichen mit einer Richtung, dazu die
 * Tafel, die den Pfeil erklärt. Zweite Seite: Grenze, Maut, Fähre, Tunnel,
 * Kehren, Schotter, Bahnübergang, Ortsdurchfahrt - acht Zeichen, die eine
 * Stelle ankündigen. Sieben und acht, jede Seite vier Reihen: die Teilung
 * fällt zufällig auch gleichmässig aus, aber sie ist nicht deshalb gewählt.
 *
 * Eine Teilung nach reinem Platz - vierzehn auf die erste, der Rest auf die
 * zweite - wäre die andere Möglichkeit gewesen. Sie hätte eine volle und eine
 * fast leere Seite ergeben, und vor allem: der Umbruch läge dann an einer
 * Stelle, die nichts bedeutet, und verschöbe sich bei jeder neuen Form. Wer
 * die Legende zweimal aufschlägt, soll beide Male dasselbe an derselben Stelle
 * finden.
 *
 * @param shapes Der Katalog, in Katalogreihenfolge.
 * @returns Eine Seite je Gruppe, leere Gruppen fallen weg.
 */
export function legendeSeiten(shapes: readonly TulipShapeInfo[]): LegendeSeite[] {
  const gruppen = [
    shapes.filter((info) => info.directions.length > 0),
    shapes.filter((info) => info.directions.length === 0),
  ].filter((gruppe) => gruppe.length > 0)

  // Die Tafel bekommt die erste Seite, die es gibt, und nicht die erste
  // Gruppe: fiele die eine weg, stünde die Anleitung sonst nirgends.
  return gruppen.map((zeichen, i) => ({ mitTafel: i === 0, zeichen }))
}

/**
 * Wie viele Legendenseiten das Buch bekommt.
 *
 * Eine Eigenschaft des Katalogs und nicht der Reise: welche Zeichen es gibt,
 * steht fest, bevor jemand eine Etappe anlegt. Ob überhaupt eine Legende
 * gedruckt wird, hängt dagegen an der Reise - siehe hasTulips.
 */
export const LEGENDE_SEITEN = legendeSeiten(TULIP_SHAPES).length

/** Die Seitenarten des Buchs. */
export type PageKind =
  | 'titel'
  | 'uebersicht'
  | 'kennzahlen'
  | 'etappe'
  | 'budget'
  | 'vignetten'
  | 'ausruestung'
  | 'notizen'
  | 'legende'
  | 'rueckseite'

export interface BookPage {
  kind: PageKind
  /** Eindeutiger Schlüssel für React. */
  key: string
  /** Seitenzahl, von 1 an. Steht so im Fuss der Seite. */
  page: number
  /** Nur bei Etappenseiten: zu welcher Etappe die Seite gehört. */
  stageId?: string
  /**
   * Nur bei Etappenseiten: die wievielte Seite dieser Etappe, von 1 an.
   * Alles über 1 ist eine Fortsetzung und bekommt die laufende Kopfzeile.
   */
  part?: number
}

export interface BookInput {
  /** Die Etappen in Reisereihenfolge, ohne die ausgelassenen. */
  stages: readonly { id: string }[]
  /** Zusatzseiten je Etappen-ID, aus planStage. Fehlt der Eintrag, ist es keine. */
  continuations: Record<string, number>
  /** Anzahl linierter Notizseiten am Ende. */
  notes: number
  /** Ohne erfasste Ausgabe entfällt die Budgetseite. */
  hasBudget: boolean
  /** Ohne eine einzige Tulpe entfällt die Legendenseite. */
  hasTulips: boolean
  /** Ohne einen einzigen Eintrag entfällt die Vignettenseite. */
  hasVignetten: boolean
  /** Ohne einen einzigen Eintrag entfällt die Ausrüstungsseite. */
  hasKit: boolean
}

/**
 * Wie viele leere Notizseiten hinten ins Buch kommen.
 *
 * Vier, nicht zwei und nicht zwanzig: es ist ein Reisetagebuch, unterwegs fällt
 * immer etwas an, das in keine Etappe gehört - Adressen, Werkstattnummern, die
 * Rechnung mit dem Nachbarcamper. Zwanzig leere Seiten wären dagegen Papier,
 * das nach der Reise leer bleibt.
 */
export const NOTIZSEITEN = 4

/**
 * Baut die Seitenfolge des ganzen Buchs.
 *
 * Titelseite, Übersichtskarte, Kennzahlen, die Etappen mit ihren
 * Fortsetzungen, das Budget, die beiden Checklisten, die Notizseiten, die
 * Legende der Zeichen, die Rückseite. Die Reihenfolge ist die Dramaturgie:
 * wohin es geht, wie weit es ist, wie es war, was es gekostet hat, was noch zu
 * tun ist.
 *
 * Die beiden Checklisten bekommen ausdrücklich je eine eigene Seite. Sie
 * zusammen auf eine zu zwingen hiesse, ihre Länge zu kennen - die Listen sind
 * aber in der App ergänzbar, und eine dreizehnte Vignette darf nicht dazu
 * führen, dass die Ersatzteile abgeschnitten werden.
 *
 * Je eine Seite bekommt aber nur, wer auch etwas darauf hat. Eine leere Liste
 * ergab bisher ein Blatt Papier mit einer Überschrift, dem Zähler "0 / 0" und
 * dem Satz "Die Liste ist leer." - zweimal, denn beide Listen sind in einer
 * neu angelegten Reise leer. Dieselbe Regel wie bei der Budgetseite und der
 * Legende.
 *
 * Die Legende steht zuletzt, unmittelbar vor der Rückseite.
 *
 * Das ist die Innenseite des hinteren Deckels: wer das Buch von hinten
 * aufschlägt - und das tut, wer etwas nachschlagen und nicht lesen will -, hat
 * sie als erstes vor sich. Vorn stünde sie zwischen Übersicht und erstem
 * Reisetag und wäre nach dem zweiten Tag begraben.
 *
 * Sie ist seit den fünfzehn Zeichen zweiseitig (LEGENDE_SEITEN), und das ändert
 * an dieser Überlegung nichts: die Richtungszeichen zuerst, die Streckenzeichen
 * unmittelbar dahinter. Wer von hinten aufschlägt, hat den ganzen Katalog nach
 * einem Blattwenden vor sich statt dreizehn Zeilen und einen Rest.
 *
 * Ob die beiden Seiten aufgeschlagen *nebeneinander* liegen, sagt diese
 * Funktion ausdrücklich nicht zu. Das hinge daran, ob die Legende auf einer
 * geraden Seite beginnt, und die Seitenzahl davor hängt an der Zahl der
 * Etappen, ihrer Fortsetzungen und der Budgetseite - in rund der Hälfte der
 * Bücher sind es also Vorder- und Rückseite desselben Blatts. Es zu erzwingen
 * hiesse, eine Vakatseite einzuschieben, und damit wäre die Regel oben - keine
 * Rücksicht auf gerade und ungerade Seiten - für den Rest des Buchs
 * mitgekippt.
 *
 * Der Ort hat ausserdem eine Eigenschaft, die keine andere Stelle im Buch hat:
 * er verschiebt keine einzige gedruckte Seitenzahl. Vor der Legende steht die
 * letzte Notizseite, nach ihr nur noch die Rückseite - und die trägt als
 * einzige Seite des Buchs keine Nummer im Fuss.
 *
 * Und sie steht nur da, wo sie etwas erklärt: hat die Reise keine einzige
 * Tulpe, kommt im ganzen Buch kein Zeichen vor, und zwei Seiten Erklärungen zu
 * nichts wären zwei Blatt Papier, die nach der Reise leer bleiben. Dieselbe
 * Regel wie bei der Budgetseite.
 */
export function bookPages(input: BookInput): BookPage[] {
  const seiten: BookPage[] = []
  let nummer = 0

  const lege = (kind: PageKind, key: string, extra: Partial<BookPage> = {}) => {
    nummer++
    seiten.push({ kind, key, page: nummer, ...extra })
  }

  lege('titel', 'titel')
  lege('uebersicht', 'uebersicht')
  lege('kennzahlen', 'kennzahlen')

  for (const stage of input.stages) {
    const zusatz = Math.max(0, Math.floor(input.continuations[stage.id] ?? 0))
    for (let teil = 1; teil <= zusatz + 1; teil++) {
      lege('etappe', `etappe-${stage.id}-${String(teil)}`, { stageId: stage.id, part: teil })
    }
  }

  if (input.hasBudget) lege('budget', 'budget')

  if (input.hasVignetten) lege('vignetten', 'vignetten')
  if (input.hasKit) lege('ausruestung', 'ausruestung')

  for (let i = 1; i <= Math.max(0, Math.floor(input.notes)); i++) {
    lege('notizen', `notizen-${String(i)}`)
  }

  if (input.hasTulips) {
    for (let teil = 1; teil <= LEGENDE_SEITEN; teil++) {
      lege('legende', `legende-${String(teil)}`, { part: teil })
    }
  }

  lege('rueckseite', 'rueckseite')

  return seiten
}
